import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import ExpiryTrackedDocumentDto from '@/models/Documents/ExpiryTrackedDocumentDto';
import DocumentSearchCriteria from '@/models/Documents/DocumentSearchCriteria';
import EntityType from '@/models/Shared/EntityType';

export interface IComplianceState {
  loading: boolean;
  workerDocuments: ExpiryTrackedDocumentDto[];
  companyDocuments: ExpiryTrackedDocumentDto[];
  locationDocuments: ExpiryTrackedDocumentDto[];
  loadingDialog: boolean;
}

@Module({ dynamic: true, store, name: 'Compliance', namespaced: true})
class Compliance extends VuexModule implements IComplianceState {
  
  public loading = false;
  public workerDocuments: ExpiryTrackedDocumentDto[] = [];
  public companyDocuments: ExpiryTrackedDocumentDto[] = [];
  public locationDocuments: ExpiryTrackedDocumentDto[] = [];
  public loadingDialog = false;

  @Action
  async searchWorkerDocuments() {
    const request = new DocumentSearchCriteria();
    request.entityType = EntityType.Worker;
    const date = new Date()
    date.setMonth(date.getMonth() + 1);
    request.expiresBefore = date.toISOString().substr(0, 10);
    this.setLoading(true);
    await ajax.post(`/api/v1/agency/documents/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setWorkerDocuments", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async searchCompanyDocuments() {
    const request = new DocumentSearchCriteria();
    request.entityType = EntityType.Company;
    const date = new Date()
    date.setMonth(date.getMonth() + 1);
    request.expiresBefore = date.toISOString().substr(0, 10);
    this.setLoading(true);
    await ajax.post(`/api/v1/agency/documents/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setCompanyDocuments", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async searchLocationDocuments() {
    const request = new DocumentSearchCriteria();
    request.entityType = EntityType.Location;
    const date = new Date()
    date.setMonth(date.getMonth() + 1);
    request.expiresBefore = date.toISOString().substr(0, 10);
    this.setLoading(true);
    await ajax.post(`/api/v1/agency/documents/search`, request)
    .then(response => {
      if (response.data === null) {
        response.data = [];
      }
      this.context.commit("setLocationDocuments", response.data);
      this.setLoading(false);
    })
    .catch(error => {
      AppModule.handleApiError(error);
      this.setLoading(false);
    });
  }

  @Action
  async downloadDocument(expiryTrackedDocument: ExpiryTrackedDocumentDto) {
    this.setLoadingDialog(true);
    let url = '';
    switch (expiryTrackedDocument.entityType){
      case EntityType.Worker:
        url = `/api/v1/agency/workers/${expiryTrackedDocument.entityId}/documents/${expiryTrackedDocument.id}`;
        break;
      case EntityType.Company:
        url = `/api/v1/agency/companies/${expiryTrackedDocument.entityId}/documents/${expiryTrackedDocument.id}`;
        break;
      case EntityType.Location:
        url = `/api/v1/agency/locations/${expiryTrackedDocument.entityId}/documents/${expiryTrackedDocument.id}`;
        break;
    }

    await ajax.get(url)
    .then(response => {
      //download the file
      const element = document.createElement('a');
      element.setAttribute('href', response.data.signedUrl);

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);          
    })
    .catch(error => {
      AppModule.handleApiError(error);
    });
  }
  
  @Action
  cleanState() {
    this.context.commit("resetState");
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.workerDocuments = [];
    this.companyDocuments = [];
    this.locationDocuments = [];
    this.loadingDialog = false;
   
  }

  @Mutation
  private async setLoading(isLoading: boolean) {
      this.loading = isLoading;
  }

  @Mutation
  private async setWorkerDocuments(documents: ExpiryTrackedDocumentDto[]) {
      this.workerDocuments = documents;
  }

  @Mutation
  private async setCompanyDocuments(documents: ExpiryTrackedDocumentDto[]) {
      this.companyDocuments = documents;
  }

  @Mutation
  private async setLocationDocuments(documents: ExpiryTrackedDocumentDto[]) {
      this.locationDocuments = documents;
  }

  @Mutation
  private async setLoadingDialog(isLoadingDialog: boolean) {
      this.loadingDialog = isLoadingDialog;
  }
}

export const ComplianceModule = getModule(Compliance)
